import React from "react";

import {
  Icon,
  AmphoraIcon,
  ArmchairIcon,
  BanknoteIcon,
  BikeIcon,
  BiohazardIcon,
  BlocksIcon,
  BookCopy,
  BookOpenIcon,
  BoxIcon,
  BrushIcon,
  CameraIcon,
  CannabisIcon,
  CarIcon,
  CigaretteIcon,
  CircuitBoardIcon,
  CogIcon,
  CoinsIcon,
  Disc3Icon,
  DumbbellIcon,
  FileTextIcon,
  FishSymbolIcon,
  FlameIcon,
  GemIcon,
  Grid2x2Icon,
  GuitarIcon,
  HeadphonesIcon,
  LampIcon,
  LuggageIcon,
  PaletteIcon,
  PenToolIcon,
  PillBottleIcon,
  PlaneIcon,
  SailboatIcon,
  ShirtIcon,
  SmartphoneIcon,
  StampIcon,
  StarIcon,
  StethoscopeIcon,
  TabletsIcon,
  TentIcon,
  TicketIcon,
  TrophyIcon,
  UsersRoundIcon,
  UtensilsCrossedIcon,
  WalletCardsIcon,
  WalletIcon,
  WatchIcon,
  WineIcon,
} from "lucide-react";
import {
  bagHand,
  belt,
  bottlePerfume,
  candlestickLit,
  coinsStack,
  cupSaucer,
  deskLamp,
  gemRing,
  glassesSun,
  golfDriver,
  hatBaseball,
  highHeel,
  motorRacingHelmet,
  scarf,
  sneaker,
  soapBar,
  targetArrow,
  tire,
  wineGlassBottle,
} from "@lucide/lab";

/**
 * VRG-628 — the canonical PRODUCT TYPE → icon map. One row per entry in
 * `ALL_PRODUCT_TYPES` (68), all of them explicit.
 *
 * ── ⚠️ WHY THIS FILE EXISTS: A SHARED NAMESPACE, NOT A MISSING MAP ─────────
 * `CategoryCell` resolves its glyph from ONE `icons` object keyed by both
 * product-CATEGORY slugs (23) and product-TYPE names (68), normalised the same
 * way. Measured on this branch, **14 keys serve both at once**:
 *
 *   footwear · handbags · eyewear · jewelry · watches · gemstones · art
 *   trading_cards · collectibles · books · caviar · cannabis
 *   pharmaceuticals · supplements
 *
 * While the two words were synonyms that was harmless. It is not harmless now,
 * and it has already bitten twice — `Cells.tsx`'s own comments record VRG-152
 * renaming the tag-shop TYPE "Fine Art" to "Art" and thereby changing the
 * unrelated general-Products CATEGORY "Art" from Amphora to Palette, and the
 * same for "Collectibles" (HeartHandshake to Star). Both are documented there
 * as cross-feature side effects. That is the defect: a rename on one axis
 * silently repaints the other, and nothing fails when it does.
 *
 * So types resolve HERE and categories resolve in `categoryIcon.tsx` /
 * `Cells.tsx`. Same normalisation, same `strokeWidth: 1`, same `<BoxIcon>`
 * fallback — the shapes match deliberately, because the two accessors are
 * siblings and reading one should teach you the other.
 *
 * ── ⚠️ WHAT THIS DELIBERATELY DOES NOT DO: CHANGE ANY GLYPH ───────────────
 * Every row below is the glyph that type ALREADY renders through `CategoryCell`
 * today, extracted verbatim. 67 of the 68 resolved to a real glyph before this
 * file existed; the only fall-through was `Other / Not Listed`, and `BoxIcon`
 * is the right answer for it. This is a de-duplication, not a re-design — so
 * the diff is provably invisible to a user, and any future glyph change is a
 * deliberate edit to one file rather than an accident of a rename elsewhere.
 *
 * `product-type-icons.guard.spec.ts` holds it to total coverage of
 * `ALL_PRODUCT_TYPES` with no dead keys.
 */
const PRODUCT_TYPE_ICONS = (p: {
  strokeWidth: 1;
  size: number;
}): Record<string, JSX.Element> => ({
  // ── Fashion & Apparel ────────────────────────────────────────────────────
  apparel_clothing: <ShirtIcon {...p} />,
  sneakers: <Icon iconNode={sneaker} {...p} />,
  footwear: <Icon iconNode={highHeel} {...p} />,
  handbags: <Icon iconNode={bagHand} {...p} />,
  wallets: <WalletIcon {...p} />,
  luggage: <LuggageIcon {...p} />,
  eyewear: <Icon iconNode={glassesSun} {...p} />,
  belts: <Icon iconNode={belt} {...p} />,
  scarves: <Icon iconNode={scarf} {...p} />,
  hats: <Icon iconNode={hatBaseball} {...p} />,

  // ── Jewelry, Watches & Precious ──────────────────────────────────────────
  jewelry: <Icon iconNode={gemRing} {...p} />,
  watches: <WatchIcon {...p} />,
  gemstones: <GemIcon {...p} />,
  // A STACK of bars, not CoinsIcon — `coins` below is its own type and the two
  // must not read as the same thing.
  bullion: <Icon iconNode={coinsStack} {...p} />,

  // ── Beauty & Personal Care ───────────────────────────────────────────────
  fragrance_perfume: <Icon iconNode={bottlePerfume} {...p} />,
  // Brush, not Palette — `art` below is Palette, and Cosmetics sharing it would
  // put one glyph on two unrelated types.
  cosmetics: <BrushIcon {...p} />,
  skincare: <Icon iconNode={soapBar} {...p} />,
  candles: <Icon iconNode={candlestickLit} {...p} />,

  // ── Art & Design ─────────────────────────────────────────────────────────
  art: <PaletteIcon {...p} />,
  sculpture: <AmphoraIcon {...p} />,
  photography: <CameraIcon {...p} />,

  // ── Collectibles ─────────────────────────────────────────────────────────
  trading_cards: <WalletCardsIcon {...p} />,
  currency: <BanknoteIcon {...p} />,
  coins: <CoinsIcon {...p} />,
  stamps: <StampIcon {...p} />,
  comics: <BookOpenIcon {...p} />,
  memorabilia: <TrophyIcon {...p} />,
  toys: <BlocksIcon {...p} />,
  // VRG-152 wanted `CircleStarIcon`; not in the pinned lucide-react@0.468.0, so
  // plain Star stands in until the pin moves (flagged there, not dropped).
  collectibles: <StarIcon {...p} />,

  // ── Music, Media & Books ─────────────────────────────────────────────────
  vinyl_records: <Disc3Icon {...p} />,
  musical_instruments: <GuitarIcon {...p} />,
  books: <BookCopy {...p} />,

  // ── Wine, Spirits & Gourmet ──────────────────────────────────────────────
  wine: <WineIcon {...p} />,
  // A bottle, against Wine's glass — they are separate types and the pair reads
  // as a distinction rather than a duplicate.
  spirits: <Icon iconNode={wineGlassBottle} {...p} />,
  gourmet_food: <UtensilsCrossedIcon {...p} />,
  coffee_tea: <Icon iconNode={cupSaucer} {...p} />,
  cigars_tobacco: <CigaretteIcon {...p} />,
  caviar: <FishSymbolIcon {...p} />,

  // ── Automotive & Transport ───────────────────────────────────────────────
  automobiles: <CarIcon {...p} />,
  auto_parts: <Icon iconNode={tire} {...p} />,
  // ⚠️ Motorcycles and Bicycles are DELIBERATELY DIFFERENT, and this is the
  // opposite of the usual note. A helmet and a bicycle are two vehicles a buyer
  // would never confuse, they sit adjacent in the same sector, and `BikeIcon`
  // is a pedal bicycle — putting it on both would make a motorcycle tag render
  // as a bicycle. If you are here to "fix" a perceived duplicate: there is
  // none. Merging them is a product decision, not a tidy-up.
  motorcycles: <Icon iconNode={motorRacingHelmet} {...p} />,
  bicycles: <BikeIcon {...p} />,
  marine_boats: <SailboatIcon {...p} />,

  // ── Electronics & Tech ───────────────────────────────────────────────────
  electronics: <SmartphoneIcon {...p} />,
  audio_equipment: <HeadphonesIcon {...p} />,

  // ── Home & Living ────────────────────────────────────────────────────────
  furniture: <ArmchairIcon {...p} />,
  // `home_décor` keeps its é: the normaliser folds whitespace and "&" only, so
  // the key must match the canonical name exactly.
  home_décor: <LampIcon {...p} />,
  rugs_carpets: <Grid2x2Icon {...p} />,
  // A desk lamp, against Home Décor's plain lamp — adjacent types, two glyphs.
  lighting: <Icon iconNode={deskLamp} {...p} />,

  // ── Sports & Outdoor ─────────────────────────────────────────────────────
  sporting_goods: <DumbbellIcon {...p} />,
  golf_equipment: <Icon iconNode={golfDriver} {...p} />,
  camping: <TentIcon {...p} />,

  // ── Regulated & Controlled ───────────────────────────────────────────────
  firearms: <Icon iconNode={targetArrow} {...p} />,
  cannabis: <CannabisIcon {...p} />,
  pharmaceuticals: <PillBottleIcon {...p} />,
  medical_devices: <StethoscopeIcon {...p} />,
  // Tablets (two pills), not Pill (one capsule) — "supplements" is plural.
  supplements: <TabletsIcon {...p} />,
  hazardous: <BiohazardIcon {...p} />,

  // ── Industrial & B2B ─────────────────────────────────────────────────────
  industrial_machinery: <CogIcon {...p} />,
  aerospace_defense: <PlaneIcon {...p} />,
  electrical_components: <CircuitBoardIcon {...p} />,

  // ── Access & Digital ─────────────────────────────────────────────────────
  memberships: <UsersRoundIcon {...p} />,
  ticketing_access: <TicketIcon {...p} />,
  // FileText is the MEDIUM (a page), which is right for the broad Documents
  // TYPE. The narrower `legal_documents` CATEGORY draws a balance scale
  // instead (VRG-611) — different axis, different glyph, on purpose.
  documents: <FileTextIcon {...p} />,
  // Same glyph as Memberships, deliberately: a credential IS a
  // membership-shaped artifact, a card a person carries. One concept, one icon.
  credentials: <UsersRoundIcon {...p} />,

  // ── Luxury Miscellany ────────────────────────────────────────────────────
  writing_instruments: <PenToolIcon {...p} />,
  lighters: <FlameIcon {...p} />,

  // ── Other ────────────────────────────────────────────────────────────────
  // The one type whose honest answer IS the generic box.
  "other_/_not_listed": <BoxIcon {...p} />,
});

/**
 * Canonical PRODUCT TYPE → icon. `type` is normalised the same way
 * `getCategoryIcon` and `CategoryCell` normalise theirs (lowercased,
 * whitespace/`&` → `_`), so the two accessors cannot disagree about what a
 * given string means. An unknown value falls back to a generic box.
 */
export const getProductTypeIcon = (type: string, size = 20): JSX.Element => {
  const p = { strokeWidth: 1 as const, size };
  const key = (type || "").trim().toLowerCase().replace(/[\s&]+/g, "_");
  return PRODUCT_TYPE_ICONS(p)[key] ?? <BoxIcon {...p} />;
};

/** The map's key set, for the drift guard. Derived, so it cannot disagree. */
export const productTypeIconKeys = (): string[] =>
  Object.keys(PRODUCT_TYPE_ICONS({ strokeWidth: 1, size: 20 }));
